import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  type TextFieldProps
} from '@mui/material'
import React, { useState } from 'react'

import Icon from 'components/Icon'

export const MonitorSearchField: React.FC<
  {
    initialValue?: string
    isLoading?: boolean
    label?: string
    onSubmit(value: string): void
  } & Omit<TextFieldProps, 'value' | 'onChange' | 'onKeyDown' | 'onSubmit'>
> = ({
  disabled,
  initialValue,
  isLoading,
  label,
  onSubmit,
  ...textFieldProps
}) => {
  const [value, setValue] = useState(initialValue ?? '')

  // State flag derivations controlling action availability
  const isClearDisabled = isLoading || !value
  const isSearchDisabled = disabled || isLoading || !value?.trim()

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLoading || event.key !== 'Enter') return
    event.preventDefault()
    onSubmit(value)
  }

  return (
    <Stack direction="row" sx={{ alignItems: 'baseline', columnGap: 2 }}>
      <TextField
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <IconButton
              disabled={isClearDisabled}
              size="small"
              onClick={() => {
                setValue('')
                onSubmit('')
              }}
            >
              <Icon icon="close" />
            </IconButton>
          )
        }}
        inputProps={{ 'data-testid': 'status-check-search-input' }}
        label={label ?? 'Search'}
        sx={{ width: 400 }}
        value={value}
        onChange={({ target }) => setValue(target.value)}
        onKeyDown={handleKeyDown}
        {...textFieldProps}
      />
      <Button
        {...(isLoading && {
          startIcon: <CircularProgress size={12} sx={{ color: 'inherit' }} />
        })}
        data-testid="status-check-search-submit-button"
        disabled={isSearchDisabled}
        size="small"
        variant="contained"
        onClick={() => {
          onSubmit(value)
        }}
      >
        {isLoading ? 'Searching...' : 'Search'}
      </Button>
    </Stack>
  )
}
